import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from "recharts";
import {
  Users, Building2, CreditCard, BarChart3, Activity, Shield,
  TrendingUp, Clock, Search, ChevronDown, Crown
} from "lucide-react";

import { API } from "../lib/api";

const PLAN_COLORS = { free: "#94A3B8", individual: "#3B82F6", team: "#0F172A", enterprise: "#7C3AED", super_admin: "#10B981" };
const PLAN_LABELS = { free: "Free", individual: "Individual", team: "Team", enterprise: "Enterprise", super_admin: "Super" };

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [changingPlan, setChangingPlan] = useState(null);

  useEffect(() => {
    if (!user?.is_super_admin) { navigate("/dashboard"); return; }
    Promise.all([
      axios.get(`${API}/api/admin/dashboard`, { withCredentials: true }),
      axios.get(`${API}/api/admin/users`, { withCredentials: true }),
    ]).then(([d, u]) => {
      setDash(d.data);
      setUsers(u.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [user, navigate]);

  const setPlan = async (userId, plan) => {
    setChangingPlan(userId);
    try {
      await axios.put(`${API}/api/admin/users/${userId}/plan`, { plan }, { withCredentials: true });
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, plan } : u));
    } catch (err) { console.error(err); }
    finally { setChangingPlan(null); }
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q);
  });

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h1 className="font-outfit text-3xl font-black text-slate-900">Super Admin</h1>
              <p className="font-ibm-plex text-sm text-slate-500">Platform control center</p>
            </div>
          </div>
          <div className="flex border border-gray-200 rounded-lg p-0.5 bg-gray-50">
            {["overview", "users", "activity"].map((t) => (
              <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
                className={`px-4 py-1.5 text-sm font-outfit font-medium rounded-md transition-all capitalize ${
                  tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}>{t}</button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {tab === "overview" && dash && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-slide-up stagger-1">
              {[
                { label: "Total Users", value: dash.total_users, icon: Users, color: "text-blue-600" },
                { label: "Organizations", value: dash.total_orgs, icon: Building2, color: "text-purple-600" },
                { label: "Assessments", value: dash.total_assessments, icon: BarChart3, color: "text-slate-900" },
                { label: "Revenue", value: `$${dash.total_revenue}`, icon: CreditCard, color: "text-emerald-600" },
              ].map((s) => (
                <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500">{s.label}</span>
                    <s.icon className={`w-4 h-4 ${s.color}`} strokeWidth={1.5} />
                  </div>
                  <div className={`font-outfit text-3xl font-black ${s.color}`}>{s.value}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Signup Trend */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-2">
                <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">User Signups (30 days)</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={dash.daily_signups}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9CA3AF" }}
                      tickFormatter={(d) => new Date(d).toLocaleDateString("en", { month: "short", day: "numeric" })} />
                    <YAxis tick={{ fontSize: 10, fill: "#9CA3AF" }} />
                    <Tooltip contentStyle={{ fontFamily: "IBM Plex Sans", fontSize: 12 }} />
                    <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Plan Distribution */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-3">
                <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Plan Distribution</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={Object.entries(dash.plan_counts).map(([k, v]) => ({ plan: PLAN_LABELS[k] || k, count: v }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="plan" tick={{ fontSize: 11, fill: "#6B7280" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {Object.keys(dash.plan_counts).map((k) => (
                        <Cell key={k} fill={PLAN_COLORS[k] || "#94A3B8"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Payments */}
            {dash.recent_payments?.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-4">
                <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Recent Subscriptions</h2>
                <div className="space-y-2">
                  {dash.recent_payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div>
                        <span className="font-ibm-plex text-sm text-slate-700">{p.email}</span>
                        <span className="font-ibm-plex text-xs text-slate-400 ml-2">{new Date(p.created_at).toLocaleDateString()}</span>
                      </div>
                      <span className="font-outfit text-sm font-bold text-emerald-600">{p.plan} · ${p.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {tab === "users" && (
          <div className="space-y-4 animate-slide-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input data-testid="admin-user-search" type="text" value={search}
                  onChange={(e) => setSearch(e.target.value)} placeholder="Search users..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <span className="font-ibm-plex text-sm text-slate-400">{filteredUsers.length} users</span>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {["Name", "Email", "Type", "Plan", "Assessments", "Joined", "Actions"].map((h) => (
                      <th key={h} className="text-left px-4 py-3 font-ibm-plex text-xs font-semibold text-slate-500 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {u.email === "alwargiridhar@gmail.com" && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                          <span className="font-outfit text-sm font-semibold text-slate-900">{u.name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-ibm-plex text-sm text-slate-500">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-ibm-plex font-medium px-2 py-0.5 rounded-full ${
                          u.account_type === "organization" ? "bg-purple-50 text-purple-700" :
                          u.account_type === "individual" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-500"
                        }`}>{u.account_type || "—"}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-outfit text-sm font-bold" style={{ color: PLAN_COLORS[u.plan] || "#94A3B8" }}>
                          {PLAN_LABELS[u.plan] || u.plan || "Free"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-outfit text-sm font-bold text-slate-700">{u.assessments || 0}</td>
                      <td className="px-4 py-3 font-ibm-plex text-xs text-slate-400">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`plan-select-${u.id}`}
                          value={u.plan || "free"}
                          onChange={(e) => setPlan(u.id, e.target.value)}
                          disabled={changingPlan === u.id || u.email === "alwargiridhar@gmail.com"}
                          className="text-xs font-ibm-plex border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-900 bg-white disabled:opacity-50"
                        >
                          {["free", "individual", "team", "enterprise"].map((p) => (
                            <option key={p} value={p}>{PLAN_LABELS[p]}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {tab === "activity" && dash && (
          <div className="space-y-6 animate-slide-up">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Logins */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <h2 className="font-outfit text-base font-bold text-slate-900">Recent Logins</h2>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {dash.recent_logins?.map((l) => (
                    <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div>
                        <span className="font-ibm-plex text-sm text-slate-700">{l.name || l.email}</span>
                        <span className="font-ibm-plex text-xs text-slate-300 ml-2">{l.method}</span>
                      </div>
                      <span className="font-ibm-plex text-xs text-slate-400">
                        {new Date(l.created_at).toLocaleString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                  {(!dash.recent_logins || dash.recent_logins.length === 0) && (
                    <p className="font-ibm-plex text-sm text-slate-400 text-center py-4">No recent logins</p>
                  )}
                </div>
              </div>

              {/* Recent Signups */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <h2 className="font-outfit text-base font-bold text-slate-900">Recent Signups</h2>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {dash.recent_signups?.map((s) => (
                    <div key={s.id} className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div>
                        <span className="font-ibm-plex text-sm text-slate-700">{s.name || s.email}</span>
                        <span className="font-ibm-plex text-xs text-slate-400 ml-2">{s.email}</span>
                      </div>
                      <span className="font-ibm-plex text-xs text-slate-400">
                        {new Date(s.created_at).toLocaleString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                  {(!dash.recent_signups || dash.recent_signups.length === 0) && (
                    <p className="font-ibm-plex text-sm text-slate-400 text-center py-4">No recent signups</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
